import { makeStyles } from "tss-react/mui"
import { Stack, Typography } from "@mui/material"
import type { DateTime } from "luxon"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"

import type {
    TakeawayCartItem,
    TakeawayProduct,
    TakeawayProductGroup as TakeawayProductGroupType
} from "../takeawayTypes"
import { TakeawayProductGroup } from "./TakeawayProductGroup"
import { TakeawayMenuContentSkeleton } from "./TakeawayMenuContentSkeleton"

const useStyles = makeStyles()((theme) => ({
    emptyWrap: {
        textAlign: "center",
        padding: "2rem 1rem",
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
    },
}))

type TakeawayMenuContentProps = Readonly<{
    isLoading: boolean
    allGroups: TakeawayProductGroupType[]
    displayGroups: TakeawayProductGroupType[]
    showFavorites: boolean
    favoriteIds: ReadonlySet<number>
    searchRegex: RegExp | undefined
    selectedDate: DateTime
    cart: TakeawayCartItem[]
    onQuantityChange: (product: TakeawayProduct, quantity: number) => void
    onFavoriteToggle: (product: TakeawayProduct, makeFavorite: boolean) => Promise<boolean>
    onDetailsClick: (product: TakeawayProduct) => void
}>

export function TakeawayMenuContent({
    isLoading,
    allGroups,
    displayGroups,
    showFavorites,
    favoriteIds,
    searchRegex,
    selectedDate,
    cart,
    onQuantityChange,
    onFavoriteToggle,
    onDetailsClick,
}: TakeawayMenuContentProps) {
    const { classes } = useStyles()
    const dt = useDateTime()

    const getCartItem = (productId: number) => cart.find((item) => item.product.id === productId)

    if (isLoading) return <TakeawayMenuContentSkeleton />

    if (!allGroups.length) {
        return (
            <Stack gap=".75rem" className={classes.emptyWrap}>
                <LocalizedStrict id="takeaway-menu-no-products-for-date" vars={{ date: dt.formatDate(selectedDate) }}>
                    <Typography variant="h6">No products available for {dt.formatDate(selectedDate)}</Typography>
                </LocalizedStrict>
            </Stack>
        )
    }

    if (!displayGroups.length) {
        return (
            <Stack gap=".75rem" className={classes.emptyWrap}>
                {showFavorites ? (
                    <>
                        <LocalizedStrict id="create-order-products-empty-favorites-title">
                            <Typography variant="h6">You do not have any favorite items</Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="create-order-products-empty-favorites-description">
                            <Typography variant="body1">
                                You can add favorite items so that you can complete an order more quickly. Add a
                                favorite item by clicking on the heart icon found on the product card
                            </Typography>
                        </LocalizedStrict>
                    </>
                ) : (
                    <LocalizedStrict id="create-order-products-empty">
                        <Typography variant="h6">No results</Typography>
                    </LocalizedStrict>
                )}
            </Stack>
        )
    }

    return (
        <Stack gap="2rem">
            {displayGroups.map((group) => (
                <TakeawayProductGroup
                    key={group.id}
                    group={group}
                    getCartItem={getCartItem}
                    favoriteIds={favoriteIds}
                    searchRegex={searchRegex}
                    onQuantityChange={onQuantityChange}
                    onFavoriteToggle={onFavoriteToggle}
                    onDetailsClick={onDetailsClick}
                />
            ))}
        </Stack>
    )
}
